import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Achievement } from '@app/model/achievement';
import { BadgeCouponCode } from '@app/model/badge-coupon-code';
import { BadgeCouponApiService } from '@app/services/api/badge-coupon-api.service';
import { SettingsService } from '@app/services/settings.service';
import { Clipboard } from '@capacitor/clipboard';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-badge-coupon-code',
    templateUrl: './badge-coupon-code.component.html',
    styleUrls: ['./badge-coupon-code.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BadgeCouponCodeComponent {
  @Input() public set badge(value: Achievement) {
    this._badge = value;
    this.initialize();
  }

  public get badge() {
    return this._badge;
  }

  @Input() public generateCode = false;

  public badgeCode: BadgeCouponCode = null;

  public isLoading = false;

  private _badge: Achievement;

  constructor(
    private readonly settingsService: SettingsService,
    private readonly translateService: TranslateService,
    private readonly toastrService: ToastrService,
    private readonly badgeCouponApiService: BadgeCouponApiService) { }


  public async copy(): Promise<void> {
    await Clipboard.write({ string: this.badgeCode.code });
    const message = this.translateService.instant('COMMON.clipboard_copied');
    this.toastrService.success(message);
  }

  private initialize(): void {
    this.settingsService.getSettings()
      .subscribe(data => {
        if (!data.features?.coupons) {
          return;
        }

        if (!this._badge?.couponId) {
          return;
        }

        this.isLoading = true;

        if (this.generateCode) {
          this.createBadgeCode();
          return;
        }

        this.loadBadgeCode();
      });
  }

  private loadBadgeCode(): void {
    this.badgeCouponApiService.getBadgeCouponCodeCollection(this._badge.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(data => this.badgeCode = data['hydra:member']?.[0]);
  }

  private createBadgeCode(): void {
    this.badgeCouponApiService.createBadgeCouponCode(this._badge.couponId, this._badge.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(data => this.badgeCode = data);
  }

}
